/**
 * ladderFill.js - place what a screenshot read into the roster ladder.
 *
 * The reader gives back a flat list of players and prices in the order they
 * appeared on the image. The ladder is a set of named slots the league defines.
 * This turns one into the other, and it is the only part of the screenshot
 * feature with decisions in it, which is why it lives here and is tested rather
 * than living inside a component.
 *
 * 🔴 THE SLOT CHIP FROM THE IMAGE IS NOT USED, EVEN THOUGH THE READER RETURNS
 * IT. Measured on a real Mock1 screenshot: the coloured pill chips scored 37 to
 * 61 confidence while the names scored 84 to 96. FLEX came back as "(EX)", BN3
 * as "GR)", BN5 as "NS)". Placing a player by the least legible token on the
 * image, when the league's own slot list is sitting right here and is certain,
 * would be trading a fact for a guess.
 *
 * 🔴 A PLAYER IS PLACED BY POSITION, STARTERS BEFORE BENCH, AND THE EXPENSIVE
 * ONES GO FIRST. Two running backs and an RB1/RB2 pair is obvious; three running
 * backs where the third belongs in FLEX is the case that decides the ordering.
 * Sorting by price first means the $49 back takes RB1 and the $2 back takes the
 * flex, which is what a person filling the form by hand would do. Unpriced picks
 * sort last among their own position rather than first, because a blank price is
 * an unknown, not a zero.
 *
 * 🔴 A ROSTER IS OFTEN MORE THAN ONE PICTURE, AND THE SECOND OVERLAPS THE FIRST.
 * Sixteen slots do not fit on a phone screen, so people screenshot the top,
 * scroll, and screenshot the bottom - and because nobody scrolls exactly one
 * screen, the middle rows appear in BOTH images. A player already on the roster
 * is therefore recognised and skipped, matched through normName so "Marvin
 * Harrison Jr." off one image and "Marvin Harrison" off the next are one man.
 * Getting this wrong does not raise an error; it produces a roster with two of
 * somebody, which is the kind of wrong that reaches a grade unnoticed.
 *
 * 🔴 AND EVERY READ RE-PLACES EVERY READ PLAYER, RATHER THAN FILLING THE GAPS
 * LEFT BY THE LAST ONE. This is the rule that a straightforward implementation
 * gets wrong, and the real fixture caught it: reading Ben's roster as two
 * overlapping halves put Malik Washington ($1) in the FLEX, because the top
 * image was placed before Michael Wilson ($2) had been seen, and a filled slot
 * was never revisited. One image gave Wilson the flex; two gave Washington. The
 * roster a person gets would depend on where they happened to scroll, which is
 * indefensible. So each read pools every player any read has produced, clears
 * their slots, and lays the whole set down again by price. Two images now land
 * exactly where one image would, and a test pins that.
 *
 * 🔴 A ROW THE USER TYPED IS PINNED AND IS NEVER RE-PLACED. Re-flowing is right
 * for rows a machine placed and wrong for rows a person did: someone who moved
 * a player into their flex on purpose has stated an intent, and quietly undoing
 * it on the next upload would be the feature arguing with them. Read rows carry
 * `fromRead`; anything without it holds its slot and its position is treated as
 * already spoken for.
 *
 * 🔴 NOTHING IS EVER INVENTED AND NOTHING IS EVER SILENTLY DROPPED. A player the
 * league has no room for comes back in `overflow` so the caller can say so out
 * loud. A row the reader could not resolve is already `unreadable` before it
 * reaches here. The one thing this file must never do is quietly lose a player
 * the user can see in their own screenshot.
 */

import {
  FLEX_ELIGIBLE,
  SFLEX_ELIGIBLE,
  buildRosterSlots,
  rosterablePositions,
} from "./rosterSlotFill.js";
import { normName } from "./playerNameNorm.js";

/**
 * Which positions may fill this slot.
 *
 * 🔴 THIS IS A COPY OF TeamEntryLadder's eligibleForSlot, AND IT IS DELIBERATE.
 * The obvious move is to import that function, and the first cut did. It drags
 * a React component, an avatar, a theme and the whole player board into a
 * module whose job is arranging a list, which means the placement logic can
 * only be tested by mounting a UI. Both read the SAME three exports from
 * rosterSlotFill.js, which is where the eligibility rules actually live, so a
 * change there still reaches the form, the engine, the board and this file
 * together. The duplicated lines are the dispatch, not the rule.
 */
function eligibleForSlot(slot, positions) {
  if (slot.pos === "FLEX") return FLEX_ELIGIBLE;
  if (slot.pos === "SFLEX") return SFLEX_ELIGIBLE;
  if (slot.pos === "BN") return [...rosterablePositions(positions)];
  return [slot.pos];
}

/**
 * How this file decides two rows are the same player. Through normName, so
 * spelling differences between two screenshots collapse to one man. Position is
 * part of the key because the pool can carry one name at two positions.
 */
function identity(row) {
  return `${normName(row.player)}|${row.pos}`;
}

function priceOf(row) {
  const v = row.price;
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

/**
 * Sort key for placing picks. Highest price first; unpriced last.
 *
 * Unpriced sorting LAST is deliberate. If it sorted first, a snake roster (no
 * prices at all) would place in read order, which is fine, but a MIXED roster
 * would let the one pick the reader failed to price jump ahead of a $64 wide
 * receiver and take WR1. The rule reads as: certainty outranks uncertainty.
 */
function byPriceDesc(a, b) {
  const pa = priceOf(a);
  const pb = priceOf(b);
  if (pa == null && pb == null) return a.order - b.order;
  if (pa == null) return 1;
  if (pb == null) return -1;
  if (pb !== pa) return pb - pa;
  return a.order - b.order;
}

/**
 * Place read entries into a ladder value.
 *
 * @param entries   what rowsToEntries produced: { player, pos, team, price,
 *                  confident, score, candidates, raw }
 * @param positions the league's slot counts, the same object the ladder draws from
 * @param existing  the ladder as it stands, from earlier reads or hand entry
 * @returns { ladder, placed, overflow, duplicates, flagged, previous }
 */
export function fillLadderFromRead(entries, positions, existing) {
  const slots = buildRosterSlots(positions || {});
  const previous = { ...(existing || {}) };

  // Rows a person typed hold their slots. Rows a read placed go back in the pool.
  const ladder = {};
  const pooled = [];
  let order = 0;
  for (const slot of slots) {
    const row = previous[slot.id];
    if (!row || !row.player || !row.pos) continue;
    if (row.fromRead) pooled.push({ ...row, order: order++ });
    else ladder[slot.id] = row;
  }

  const duplicates = [];
  const byId = new Map(pooled.map((r) => [identity(r), r]));

  for (const e of entries || []) {
    if (!e || !e.player || !e.pos) continue;
    const key = identity(e);
    const seen = byId.get(key);
    if (!seen) {
      byId.set(key, { ...e, order: order++ });
      continue;
    }
    // The overlapping half of a second screenshot. Not a new player.
    duplicates.push(e);
    // 🔴 The one thing a repeat may do is COMPLETE a price. A top screenshot
    // can clip a row and lose its dollar figure while the bottom carries it
    // whole. Filling a blank is strictly more information; overwriting a price
    // that is already there is not, and would let a worse read beat a better one.
    if (priceOf(seen) == null && priceOf(e) != null) seen.price = e.price;
    // Same for a name the second read was more sure about.
    if (!seen.confident && e.confident) {
      seen.player = e.player;
      seen.pos = e.pos;
      seen.team = e.team || seen.team;
      seen.confident = true;
      seen.score = e.score;
      seen.candidates = e.candidates;
    }
  }

  const queue = [...byId.values()].sort(byPriceDesc);
  const placed = [];
  const taken = new Set();

  /**
   * 🔴 THE TWO PASSES ARE THE WHOLE ALGORITHM. A single pass in slot order would
   * let the first running back land in FLEX if FLEX happened to come first, and
   * then RB2 would sit empty with a player available for it. Named slots claim
   * their positions first; FLEX, SUPERFLEX and the bench take what is left.
   */
  function pass(accepts) {
    for (const slot of slots) {
      if (ladder[slot.id]) continue;
      const eligible = eligibleForSlot(slot, positions);
      if (!accepts(slot)) continue;
      const hit = queue.find((e) => !taken.has(e.order) && eligible.includes(e.pos));
      if (!hit) continue;
      taken.add(hit.order);
      ladder[slot.id] = {
        player: hit.player,
        pos: hit.pos,
        team: hit.team || null,
        sleeperId: hit.sleeperId || null,
        // The ladder holds prices as STRINGS, because it holds what a person
        // typed. An unpriced read is an empty field, never a zero.
        price: hit.price === "" || hit.price == null ? "" : String(hit.price),
        // Marks this row as re-placeable by a later read. A hand-typed row has
        // no such mark and is pinned.
        fromRead: true,
      };
      placed.push({ ...hit, slotId: slot.id, slotLabel: slot.label });
    }
  }

  pass((slot) => slot.pos !== "FLEX" && slot.pos !== "SFLEX" && slot.pos !== "BN");
  pass((slot) => slot.pos === "FLEX" || slot.pos === "SFLEX" || slot.pos === "BN");

  const overflow = queue
    .filter((e) => !taken.has(e.order))
    .sort((a, b) => a.order - b.order);

  return {
    ladder,
    placed,
    overflow,
    duplicates,
    // 🔴 THE UNDO IS A SNAPSHOT, NOT A LIST OF EDITS. Because a read re-places
    // every read row, "what this call changed" is not a short list of slots and
    // trying to describe it as one is how an undo silently leaves a roster in a
    // state neither read produced. The whole prior ladder is small, so keep it.
    previous,
    flagged: placed.filter((e) => !e.confident).length,
  };
}

/**
 * Take one read back out, restoring the ladder exactly as it stood before it.
 *
 * 🔴 THIS IS WHY A READ IS SAFE TO TRY. Without it, a screenshot of the wrong
 * team, or a photo that read badly, could only be recovered from by starting the
 * roster again - which would make the honest advice "do not use this on a roster
 * you have already filled in". Restoring the snapshot is what lets the upload be
 * offered without a warning attached.
 */
export function undoRead(record) {
  return { ...((record && record.previous) || {}) };
}

/**
 * The sentence shown above the ladder after a read.
 *
 * 🔴 IT NEVER SAYS "DONE". The reader is a first draft and the copy has to say
 * so, or a wrong row that nobody was asked about becomes a wrong grade the user
 * cannot see. Every branch below either asks for a check or reports a loss.
 */
export function readSummary({ placed, overflow, flagged, unreadable, duplicates }) {
  const n = placed.length;
  const parts = [];
  parts.push(n === 1 ? "Read 1 player." : `Read ${n} players.`);
  const repeats = (duplicates || []).length;
  if (repeats > 0) {
    parts.push(
      repeats === 1
        ? "1 was already on the roster."
        : `${repeats} were already on the roster.`
    );
  }
  if (flagged > 0) {
    parts.push(
      flagged === 1
        ? "Check the highlighted row, I was not sure about it."
        : `Check the ${flagged} highlighted rows, I was not sure about those.`
    );
  }
  const lost = (unreadable || []).length;
  if (lost > 0) {
    parts.push(lost === 1 ? "1 line I could not read." : `${lost} lines I could not read.`);
  }
  if (overflow && overflow.length > 0) {
    parts.push(
      overflow.length === 1
        ? "1 player had no open slot."
        : `${overflow.length} players had no open slot.`
    );
  }
  return parts.join(" ");
}

/**
 * Whether a read looks like the wrong screen rather than a bad read.
 *
 * 🔴 THERE IS A DIFFERENCE, AND THE USER CANNOT SEE IT. A roster list that read
 * imperfectly and a weekly lineup page that was never going to work both come
 * back as a half-filled form, and "check the highlighted rows" is useless
 * advice for the second one. Measured Sep 1: Yahoo's Team tab produced 8
 * players, 7 of them uncertain, 67 unreadable lines, and two men who are not on
 * the roster at all - junk from an ad banner matching real names. That is not a
 * roster the reader struggled with, it is a page with no roster on it.
 *
 * The signal is the RATIO, not any single count. A genuine roster capture reads
 * mostly clean and leaves a handful of headers behind; a wrong screen leaves
 * more rubbish than players, or resolves almost nothing confidently.
 */
export function looksLikeWrongScreen({ placed, flagged, unreadable }) {
  const found = (placed || []).length;
  const junk = (unreadable || []).length;
  if (found === 0) return junk > 0;
  // Twice as much rubbish as roster.
  if (junk >= found * 2 && junk >= 8) return true;
  // Almost nothing it was sure about, on a page full of noise.
  if (found >= 3 && (flagged || 0) / found > 0.6 && junk >= found) return true;
  return false;
}

/** What to say when the picture was never going to work. */
export const WRONG_SCREEN_HELP =
  "That does not look like a roster list. On Yahoo, open your league, tap Draft, " +
  "then Draft Results, and stay on the Teams tab. A weekly lineup page will not " +
  "work, because it does not show what anyone paid.";
